import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
	IUserUpdateResponse,
	IVerifyOtpResponse,
} from '../../interfaces/account/IAccount.ts'
import { IAuthUserResponse } from '../../interfaces/account/IAuthUserResponse.ts'
import { ICurrencies } from '../../interfaces/account/ICurrencies.ts'
import { IProfileResponse } from '../../interfaces/account/IProfileResponse.ts'
import {
	forgotPasswordAction,
	getCurrenciesAction,
	getProfileData,
	getProfileDataAction,
	resetPasswordAction,
	signInAction,
	signupAction,
	verifyOtpAction,
} from './accountActions.ts'

interface initialState {
	authUser: IAuthUserResponse
	userData: IProfileResponse
	address: string
	currencies: ICurrencies
	centre: {
		lat: number
		lng: number
	}
	verifyOtpResponse?: IVerifyOtpResponse
	loaders: {
		signupLoader: boolean
		verifyingOtp: boolean
		creatingAccount: boolean
		signingIn: boolean
		forgotPasswordLoading: boolean
		passwordResetting: boolean
		gettingUserProfile: boolean
		gettingProfileData: boolean
	}
	userProfile?: IUserUpdateResponse
}

const initialState: initialState = {
	address: '',
	centre: {
		lat: 0,
		lng: 0,
	},
	currencies: {
		success: false,
		currencies: [],
	},
	authUser: {
		success: true,
		user: {
			preferences: {
				currency: {
					name: '',
					code: '',
				},
			},
			personalInformation: {
				idCard: {
					status: '',
					back: '',
					front: '',
					seen: false
				},
				personalAddress: {
					formattedAddress: '',
					latitude: 0,
					longitude: 0,
					country: '',
					isoCode: '',
					state: '',
					city: '',
					coordinates: [],
				},
				firstName: '',
				lastName: '',
				phoneNumber: '',
				dob: '',
				gender: '',
				profilePic: '',
			},
			deviceInfo: {
				fcm: '',
			},
			credentialDetails: {
				email: '',
				googleId: '',
			},
			verifyCredentials: {
				verifyStatus: false,
				mobileVerifyStatus: false,
			},
			driverAccount: {
				status: '',
				rating: {
					numRating: 0,
					totalRating: 0,
				},
				vehicleDetails: {
					make: '',
					model: '',
					color: '',
					capacity: '',
					vehicleLiscenceFront: '',
					vehicleLiscenceBack: '',
					vehicleImages: [],
					registrationNo: '',
					vehicleImage: '',
					vehicleLicenseBack: '',
					vehicleLicenseFront: ''
				},
				seen: false
			},
			wallet: {
				balance: {
					amount: 0,
					currency: '',
				},
			},
			_id: '',
			isOnline: false,
			selectedCurrency: '',
			role: '',
			location: {
				type: '',
			},
			accountActive: false,
			loyaltyPoints: 0,
			createdAt: '',
			updatedAt: '',
			__v: 0,
			verificationStatus: false,
			position: {
				type: '',
				coordinates: [],
				longitude: 0,
				latitude: 0,
				heading: 0,
				speed: 0,
				updatedAt: '',
			},
			buyerAccount: {
				deliverAddress: {
					formattedAddress: '',
					latitude: 0,
					longitude: 0,
					country: '',
					isoCode: '',
					state: '',
					city: '',
					coordinates: [],
				},
			},
			recentProductSearches: [],
			recentFoodSearches: [],
			primaryBusiness: {
				address: {
					formattedAddress: '',
					latitude: 0,
					longitude: 0,
					country: '',
					isoCode: '',
					state: '',
					city: '',
					type: '',
					coordinates: []
				},
				rating: {
					sales: 0,
					totalRating: 0,
					numRating: 0
				},
				_id: '',
				userId: '',
				title: '',
				image: '',
				type: '',
				registrationNumber: '',
				phoneNumber: '',
				category: '',
				email: '',
				description: '',
				verificationStatus: '',
				businessDescription: '',
				updatedAt: ''
			},
			myAddresses: [],
		},
		token: '',
	},
	userData: {
		success: false,
		user: {
			personalInformation: {
				idCard: {
					front: '',
					back: '',
					description: '',
					status: '',
					seen: false,
				},
				personalAddress: {
					formattedAddress: '',
					latitude: 0,
					longitude: 0,
					country: '',
					isoCode: '',
					type: '',
					coordinates: [],
					state: '',
					city: '',
				},
				firstName: '',
				lastName: '',
				dob: '',
				gender: '',
				profilePic: '',
				phoneNumber: '',
			},
			deviceInfo: {
				fcm: '',
			},
			position: {
				type: '',
				coordinates: [],
				longitude: 0,
				latitude: 0,
				heading: 0,
				speed: 0,
				updatedAt: '',
			},
			credentialDetails: {
				email: '',
				googleId: '',
			},
			verifyCredentials: {
				verifyStatus: false,
				mobileVerifyStatus: false,
			},
			preferences: {
				currency: {
					code: '',
					name: '',
				},
			},
			driverAccount: {
				rating: {
					totalRating: 0,
					numRating: 0,
				},
				seen: false,
			},
			wallet: {
				balance: {
					amount: 0,
					currency: '',
				},
			},
			_id: '',
			isOnline: false,
			role: '',
			accountActive: false,
			loyaltyPoints: 0,
			createdAt: '',
			updatedAt: '',
			__v: 0,
			myAddresses: [],
		},
	},
	loaders: {
		signupLoader: false,
		verifyingOtp: false,
		creatingAccount: false,
		signingIn: false,
		forgotPasswordLoading: false,
		passwordResetting: false,
		gettingUserProfile: false,
		gettingProfileData: false,
	},
	verifyOtpResponse: {
		success: false,
		message: '',
		token: '',
	},
	userProfile: {
		success: false,
		user: {
			preferences: {
				currency: {
					name: '',
					code: '',
				},
			},
			_id: '',
			personalInformation: {
				firstName: '',
				lastName: '',
				phoneNumber: '',
				gender: '',
				dob: '',
				profilePic: '',
				personalAddress: {
					formattedAddress: '',
					latitude: 0,
					longitude: 0,
					country: '',
					isoCode: '',
					state: '',
					city: '',
					coordinates: [0, 0],
				},
				idCard: {
					status: '',
					back: '',
					front: '',
					seen: false
				},
			},
			credentialDetails: {
				email: '',
				googleId: '',
			},
			location: {
				type: 'Point',
			},
			verifyCredentials: {
				verifyStatus: false,
				mobileVerifyStatus: false,
			},
			accountActive: false,
			role: '',
			buyerAccount: {
				deliverAddress: {
					formattedAddress: '',
					latitude: 0,
					longitude: 0,
					country: '',
					isoCode: '',
					state: '',
					city: '',
					coordinates: [0, 0],
				},
			},

			driverAccount: {
				rating: {
					numRating: 0,
					totalRating: 0,
				},
				status: '',
				vehicleDetails: {
					make: '',
					model: '',
					color: '',
					capacity: '',
					vehicleLiscenceFront: '',
					vehicleLiscenceBack: '',
					vehicleImages: [],
					registrationNo: '',
					vehicleImage: '',
					vehicleLicenseBack: '',
					vehicleLicenseFront: ''
				},
				seen: false
			},
			selectedCurrency: '',
			position: {
				type: '',
				coordinates: [0, 0],
				updatedAt: '',
				latitude: 0,
				longitude: 0,
				heading: 0,
				speed: 0,
			},
			deviceInfo: {
				fcm: '',
			},
			isOnline: false,
			loyaltyPoints: 0,
			wallet: {
				balance: {
					amount: 0,
					currency: ''
				}
			},
			createdAt: '',
			updatedAt: '',
			__v: 0,
			verificationStatus: false,
			recentProductSearches: [],
			recentFoodSearches: [],
			primaryBusiness: {
				address: {
					formattedAddress: '',
					latitude: 0,
					longitude: 0,
					country: '',
					isoCode: '',
					state: '',
					city: '',
					type: '',
					coordinates: []
				},
				rating: {
					sales: 0,
					totalRating: 0,
					numRating: 0
				},
				_id: '',
				userId: '',
				title: '',
				image: '',
				type: '',
				registrationNumber: '',
				phoneNumber: '',
				category: '',
				email: '',
				description: '',
				verificationStatus: '',
				businessDescription: '',
				updatedAt: ''
			},
			myAddresses: []
		},
	},
}

const accountSlice = createSlice({
	name: 'account',
	initialState,
	reducers: {
		setNavbarAddress: (state, action) => {
			state.address = action.payload
		},
		setCentre: (state, action) => {
			state.centre = action.payload
		},
		setCurrentBusiness: (state, { payload }: PayloadAction<string>) => {
			if (state.authUser.user.primaryBusiness) {
				state.authUser.user.primaryBusiness._id = payload;
			}
		},
	},
	extraReducers: (builder) => {
		builder
			// sign up account
			.addCase(signupAction.pending, (state) => {
				state.loaders.signupLoader = true
			})
			.addCase(signupAction.fulfilled, (state, action) => {
				state.loaders.signupLoader = false
				if (!action.payload) return
				state.authUser = action.payload
			})
			.addCase(signupAction.rejected, (state) => {
				state.loaders.signupLoader = false
			})
			// verify otp
			.addCase(verifyOtpAction.pending, (state) => {
				state.loaders.verifyingOtp = true
			})
			.addCase(verifyOtpAction.fulfilled, (state, action) => {
				state.loaders.verifyingOtp = false
				state.verifyOtpResponse = action.payload
			})
			.addCase(verifyOtpAction.rejected, (state) => {
				state.loaders.verifyingOtp = false
			})
			// sign in
			.addCase(signInAction.pending, (state) => {
				state.loaders.signingIn = true
			})
			.addCase(signInAction.fulfilled, (state, action) => {
				state.loaders.signingIn = false
				if (!action.payload) return
				state.authUser = action.payload
			})
			.addCase(signInAction.rejected, (state) => {
				state.loaders.signingIn = false
			})
			// forgot password
			.addCase(forgotPasswordAction.pending, (state) => {
				state.loaders.forgotPasswordLoading = true
			})
			.addCase(forgotPasswordAction.fulfilled, (state) => {
				state.loaders.forgotPasswordLoading = false
			})
			.addCase(forgotPasswordAction.rejected, (state) => {
				state.loaders.forgotPasswordLoading = false
			})
			// forgot password
			.addCase(resetPasswordAction.pending, (state) => {
				state.loaders.passwordResetting = true
			})
			.addCase(resetPasswordAction.fulfilled, (state) => {
				state.loaders.passwordResetting = false
			})
			.addCase(resetPasswordAction.rejected, (state) => {
				state.loaders.passwordResetting = false
			})
			// forgot password
			.addCase(getProfileDataAction.pending, (state) => {
				state.loaders.gettingUserProfile = true
			})
			.addCase(getProfileDataAction.fulfilled, (state, action) => {
				state.loaders.gettingUserProfile = false
				state.userProfile = action?.payload
			})
			.addCase(getProfileDataAction.rejected, (state) => {
				state.loaders.gettingUserProfile = false
			})
			// get currencies
			.addCase(getCurrenciesAction.pending, (state) => {
				state.loaders.gettingUserProfile = true
			})
			.addCase(getCurrenciesAction.fulfilled, (state, action) => {
				state.loaders.gettingUserProfile = false
				if (!action.payload) return
				state.currencies = action?.payload
			})
			.addCase(getCurrenciesAction.rejected, (state) => {
				state.loaders.gettingUserProfile = false
			})

			// get profile data
			.addCase(getProfileData.pending, (state) => {
				state.loaders.gettingProfileData = true
			})
			.addCase(getProfileData.fulfilled, (state, action) => {
				state.loaders.gettingProfileData = false
				if (!action.payload) return
				state.userData = action?.payload
			})
			.addCase(getProfileData.rejected, (state) => {
				state.loaders.gettingProfileData = false
			})
	},
})

export const accountReducer = accountSlice.reducer
export const { setNavbarAddress, setCentre, setCurrentBusiness } =
	accountSlice.actions
